import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import type { ProductListItem } from "../../types/Product";
import { getProductImageUrl } from "../../utils/imageUtils";
import { openGuestRetailerDeal, trapDialogFocus } from "../guest-deals/GuestDealsUi";
import { getGuestDealDiscount, getGuestDealPrice } from "../guest-deals/GuestDealsUi.utils";
import { HF, LockIcon, cleanName } from "./homeFeedUi";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export type DealGateAction = "alert" | "save" | "history";

/**
 * Logged-out deal detail for the home feed. Shows the online deal and sends
 * the shopper to the retailer through the affiliate link; alerts, saves and
 * price history need an account.
 */
export default function DealDetailModal({
  product,
  storeLabel,
  storeColor,
  introLabel,
  priceLabel,
  instoreUrl,
  onClose,
  onGate,
  onOutbound,
  onInstore,
}: {
  product: ProductListItem;
  storeLabel: string;
  storeColor: string;
  introLabel: string;
  priceLabel: string;
  instoreUrl: string;
  onClose: () => void;
  onGate: (action: DealGateAction) => void;
  onOutbound: (url: string) => void;
  onInstore: () => void;
}) {
  const price = getGuestDealPrice(product);
  const discount = getGuestDealDiscount(product);
  const msrp = Number(product.msrp) || 0;
  const savings = msrp > price && price > 0 ? msrp - price : 0;
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      trapDialogFocus(event, dialogRef.current);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
      previouslyFocused?.focus();
    };
  }, []);

  const openRetailer = async () => {
    if (opening) return;
    setOpening(true);
    setFailed(false);
    const opened = await openGuestRetailerDeal(product, onOutbound);
    setOpening(false);
    setFailed(!opened);
  };

  const image = getProductImageUrl(product.image, "600");

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-[var(--hf-overlay)] backdrop-blur-[2px] sm:items-center sm:p-5"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      style={{ fontFamily: HF.font, color: HF.ink }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[94svh] w-full max-w-[760px] overflow-y-auto rounded-t-[26px] bg-[var(--hf-surface)] shadow-[0_24px_60px_var(--hf-shadow)] sm:rounded-[26px]"
        data-testid="deal-detail-modal"
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 bg-[var(--hf-surface)]/95 px-4 pb-2 pt-4 backdrop-blur sm:px-6">
          <span className="flex h-8 min-w-0 items-center gap-2 rounded-[10px] px-3 text-[13px] font-semibold" style={{ background: HF.bg }}>
            <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: storeColor }} />
            <span className="truncate">{storeLabel} · online price</span>
          </span>
          <span className="flex-1" />
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close deal details"
            className="flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-[var(--hf-hover-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]"
            style={{ background: HF.bg, color: HF.text2 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <div className="grid gap-5 px-4 pb-5 pt-2 sm:grid-cols-[280px_1fr] sm:gap-7 sm:px-6 sm:pb-6">
          <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-[18px]" style={{ background: HF.tile }}>
            {image && !imageFailed ? (
              <img src={image} alt="" onError={() => setImageFailed(true)} className="h-[84%] w-[84%] object-contain mix-blend-multiply" />
            ) : null}
            {discount > 0 && (
              <span className="absolute bottom-3 left-3 flex h-6 items-center rounded-full px-2.5 text-xs font-extrabold text-white" style={{ background: HF.discount }}>
                -{discount}%
              </span>
            )}
          </div>

          <div className="flex min-w-0 flex-col">
            {product.adjustedCategory && (
              <span className="text-[12px] font-bold uppercase tracking-[0.6px]" style={{ color: HF.store }}>{product.adjustedCategory}</span>
            )}
            <h2 id={titleId} className="mt-1 text-[20px] font-bold leading-[26px] tracking-[-0.3px] sm:text-[22px] sm:leading-7">{cleanName(product.name)}</h2>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
              <span className="text-[34px] font-bold tabular-nums tracking-[-0.8px]" style={{ color: HF.price }}>
                {price > 0 ? money.format(price) : "See price"}
              </span>
              {savings > 0 && <span className="text-base tabular-nums line-through" style={{ color: HF.muted }}>{money.format(msrp)}</span>}
            </div>
            {savings > 0 && (
              <span className="text-[14px] font-semibold" style={{ color: HF.price }}>Save {money.format(savings)} vs MSRP</span>
            )}

            <button
              type="button"
              onClick={() => void openRetailer()}
              disabled={opening}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-[14px] text-[15px] font-bold text-white transition hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
              style={{ background: HF.brand }}
            >
              {opening ? "Opening…" : `View on ${storeLabel}`}
              {!opening && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8" /></svg>
              )}
            </button>
            {failed && <p className="mt-2 text-center text-xs font-semibold" style={{ color: HF.red }}>This store link is unavailable right now.</p>}
            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={() => onGate("alert")}
                className="flex h-11 items-center justify-center gap-2 rounded-[14px] border text-[14px] font-bold transition hover:bg-[var(--hf-violet-soft)]"
                style={{ borderColor: HF.violetBorder, color: HF.brandText }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
                Price alert
              </button>
              <button
                type="button"
                onClick={() => onGate("save")}
                className="flex h-11 items-center justify-center gap-2 rounded-[14px] border text-[14px] font-bold transition hover:bg-[var(--hf-heart-soft)]"
                style={{ borderColor: HF.border, color: HF.heart }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinejoin="round" aria-hidden="true"><path d="M12 20s-7-4.4-9-8.6C1.6 8.4 3.6 5 7 5c2 0 3.3 1.1 5 3 1.7-1.9 3-3 5-3 3.4 0 5.4 3.4 4 6.4-2 4.2-9 8.6-9 8.6z" /></svg>
                Save
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t px-4 py-4 sm:px-6" style={{ borderColor: HF.hairline }}>
          <button
            type="button"
            onClick={() => onGate("history")}
            className="flex items-center gap-3 rounded-[16px] border p-3.5 text-left transition hover:bg-[var(--hf-hover)]"
            style={{ borderColor: HF.border }}
          >
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: HF.violetSoft, color: HF.store }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></svg>
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[14.5px] font-bold">Price history</span>
              <span className="text-[13px]" style={{ color: HF.text3 }}>See every price change for this item. Sign up to view.</span>
            </span>
            <LockIcon size={15} color={HF.store} />
          </button>
          <Link
            to={instoreUrl}
            onClick={onInstore}
            className="flex flex-col gap-2 rounded-[16px] border p-3.5 no-underline sm:flex-row sm:items-center sm:gap-3"
            style={{ background: HF.amberBg, borderColor: HF.amberBorder, color: HF.ink }}
          >
            <span className="flex min-w-0 flex-1 items-start gap-2.5">
              <span className="mt-0.5"><LockIcon size={15} color={HF.amberText} /></span>
              <span className="text-[13.5px] leading-[19px]">
                <b>Don’t miss in-store clearance near you.</b> Walmart and HD shelf markdowns of 70%+ off, with price, aisle and stock.
              </span>
            </span>
            <span className="flex h-9 flex-shrink-0 items-center justify-center rounded-full px-4 text-[13.5px] font-bold text-white" style={{ background: HF.brand }} title={`Then ${priceLabel}/mo. Cancel anytime.`}>
              Unlock for {introLabel}
            </span>
          </Link>
        </div>
      </section>
    </div>,
    document.body,
  );
}
